import React, { useCallback, useMemo } from 'react'
import { noop } from 'lodash-es'
import { DealActions, DealDetail, DealServicingStartCloseOpenAttributes, DealStatus, NegotiationFieldMeta, Severity } from 'api'
import { ModalFooter, ModalWindow, Table } from 'view/components/panels'
import { DialogField, DialogRow } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { DateLike } from 'utils'
import { createDealDetailsByIdsSelector } from 'selectors/dealSelectors'
import { Locale, Translation } from 'locale'
import { canServicingCancel, canServicingRevertAttributes } from 'view/routes/deals/components/utils'
import { Content } from '../servicingDialogStyles'
import { useSelector, useDispatch } from 'react-redux'
import { SetAllEffectiveDates } from './SetAllEffectiveDates'
import { ClonedComponentByShowModal, NotificationType, useModal } from 'view/shared/modal'
import { bulkCloseOpenOptions, useBulkCloseOpenColumns } from './BulkCloseOpenGridModel'
import { useOnMount, useUserPreference } from 'view/hooks'

interface ServicingBulkCloseOpenDialogComponentProps extends ClonedComponentByShowModal {
  adminCloseOpen: boolean
  canSend: (dealDetail: DealDetail) => boolean
  dealIds: string[]
}

export const ServicingBulkCloseOpenDialog: React.FC<ServicingBulkCloseOpenDialogComponentProps> = ({
  adminCloseOpen,
  canSend,
  dealIds,
  onClose = noop
}) => {
  const { showDialog } = useModal()
  const getDealDetailsById = useSelector(createDealDetailsByIdsSelector)
  const dealDetails = useMemo(() => getDealDetailsById(dealIds), [dealIds, getDealDetailsById])
  const userPreference = useUserPreference()
  const dispatch = useDispatch()

  const title = `${Locale.translate(Translation.TC_CLOSE_OPEN)} (${dealDetails.length})`
  const dealDetailsChanged = dealDetails.some(canServicingRevertAttributes)
  const validationIssues = dealDetails.flatMap(dealDetail => dealDetail.servicingSideDetails?.validationIssues ?? [])
  const hasValidationIssues = validationIssues.filter(validationIssue => validationIssue.severity === Severity.ERROR).length > 0

  const handleUpdateField = useCallback(
    ({ dealId, value, field, rawValue }: { dealId: string; value: any; field: NegotiationFieldMeta; rawValue?: any }) => {
      dispatch(
        DealActions.servicingUpdateAttributes({
          dealId,
          field,
          updatedValue: value,
          updatedRawValue: rawValue,
          decisionMakerOrientedByNegotiationSide: true,
          meta: { haircutSign: userPreference.haircutSign },
          revertFirst: false
        })
      )
    },
    [dispatch, userPreference.haircutSign]
  )

  const columns = useBulkCloseOpenColumns(handleUpdateField)

  const handleSetAllEffectiveDates = (effectiveDate: DateLike) => {
    dealDetails.forEach(dealDetail => {
      handleUpdateField({
        dealId: dealDetail.deal.dealId,
        value: effectiveDate,
        field: NegotiationFieldMeta.START_DATE
      })
    })
  }

  const adminSend = async () => {
    if (
      (await showDialog({
        buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO],
        type: NotificationType.Confirm,
        title: Translation.TC_CLOSE_OPEN_TRADE,
        messageText: Locale.translate(Translation.SC_CLOSE_OPEN_CONFIRMATION)
      })) === Translation.TC_BUTTON_YES
    ) {
      dealDetails.forEach(dealDetail => {
        dispatch(DealActions.servicingAccept(dealDetail.deal.dealId))
      })
      onClose()
    }
  }

  const traderSend = async () => {
    dealDetails.forEach(dealDetail => {
      dispatch(DealActions.servicingSend(dealDetail.deal.dealId))
    })
    onClose()
  }

  const handleSend = async () => {
    adminCloseOpen ? adminSend() : traderSend()
  }

  const handleCancel = async () => {
    dealDetails.forEach(dealDetail => {
      if (canServicingCancel(dealDetail)) {
        dispatch(DealActions.servicingCancel(dealDetail.deal.dealId))
      }
    })
    onClose()
  }

  useOnMount(() => {
    const dealIds = dealDetails
      .filter(dealDetail => dealDetail.deal.status !== DealStatus.SERVICING_WITH_INITIATOR)
      .map(dealDetail => dealDetail.deal.dealId)

    if (dealIds.length > 0) {
      const dealServicingStartCloseOpenAttributesList: DealServicingStartCloseOpenAttributes[] = dealIds.map(dealId => ({
        dealId: dealId
      }))
      const startRerate = async () => {
        const result = await showDialog({
          type: NotificationType.Confirm,
          title: Translation.TC_NEW_OR_OLD_COLLATERAL_PRICE,
          message: Translation.TC_EMPTY_HEADER,
          buttons: [Translation.TC_BUTTON_YES, Translation.TC_BUTTON_NO, Translation.TC_BUTTON_CANCEL],
          defaultButton: Translation.TC_BUTTON_YES
        })
        if (result === Translation.TC_BUTTON_CANCEL) {
          handleCancel()
          return
        }
        adminCloseOpen
          ? dispatch(
              DealActions.servicingStartAdminCloseOpenBulk({ dealIds, recalculateCollateralPrice: result !== Translation.TC_BUTTON_YES })
            )
          : dispatch(
              DealActions.servicingStartCloseOpenBulk({
                dealServicingStartCloseOpenAttributesList,
                recalculateCollateralPrice: result !== Translation.TC_BUTTON_YES
              })
            )
      }
      startRerate()
    }
  })

  return (
    <ModalWindow onEscKey={handleCancel} title={title}>
      <Content size="big">
        <SetAllEffectiveDates dealDetails={dealDetails} extendMaturityDate={true} onApply={handleSetAllEffectiveDates} />
        <Table columns={columns} rows={dealDetails} options={bulkCloseOpenOptions} />
      </Content>
      <ModalFooter>
        <DialogRow>
          <DialogField>
            <TextButton type={TextButtonType.Tertiary} onClick={handleCancel}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
          <DialogField flex={0}>
            <TextButton
              type={TextButtonType.Primary}
              onClick={handleSend}
              disabled={hasValidationIssues || !dealDetailsChanged || dealDetails.some(d => !canSend(d))}>
              {Locale.translate(Translation.TC_BUTTON_SEND_REQUEST)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}
